import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BookOpen, PlayCircle, ChevronRight, ChevronDown, ClipboardList, RotateCcw, Settings } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { agruparPorSecao, useVideosTreinamento } from './videos';
import { Questionario } from './Questionario';
import { ModalResultados } from './ModalResultados';
import { ModalGerenciarVideos } from './ModalGerenciarVideos';

// Modulo Manual > Videos de Treinamento (29/09/2026). Mesmo desenho do Manual escrito (lista a
// esquerda, conteudo a direita) pra quem conhece um reconhecer o outro. Depois de cada video vem
// o questionario (decisao do Ruan: so REGISTRA, nao trava o proximo — migration 0303); os
// resultados da equipe abrem pelo botao do topo, so' pra admin/gerente. Videos e perguntas vem
// do banco (0304) e se editam em "Gerenciar videos", so' admin. A lista e' ENCADEADA por secao
// (0305, pedido dele): "1. Orcamentos" > "1.01 Seu primeiro orcamento"; cada secao abre e fecha,
// e a do video aberto ja vem aberta.
export function TreinamentoPage() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const podeVerResultados = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
  const podeGerenciar = usuario?.perfil === 'admin';
  const [resultadosAbertos, setResultadosAbertos] = useState(false);
  const [gerenciarAberto, setGerenciarAberto] = useState(false);
  const { videos, erro, recarregar } = useVideosTreinamento();

  const secoes = useMemo(() => agruparPorSecao(videos ?? []), [videos]);
  // Ordem de assistir = a lista achatada (o "Proximo video" atravessa de uma secao pra outra).
  const lista = useMemo(() => secoes.flatMap((s) => s.videos), [secoes]);
  const atual = useMemo(() => lista.find((v) => v.slug === slug) ?? lista[0], [lista, slug]);
  const posicao = atual ? lista.indexOf(atual) : -1;
  const proximo = posicao >= 0 ? lista[posicao + 1] : undefined;

  const [abertas, setAbertas] = useState<Set<number>>(new Set());
  // A secao do video aberto sempre aparece aberta (inclusive ao chegar pelo "Proximo video").
  useEffect(() => {
    if (atual) setAbertas((a) => (a.has(atual.secao_id) ? a : new Set(a).add(atual.secao_id)));
  }, [atual]);
  const alternar = (id: number) => setAbertas((a) => {
    const n = new Set(a);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  // Quadro do fim do video: some ao trocar de video ou ao dar play de novo.
  const [terminou, setTerminou] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const questionarioRef = useRef<HTMLDivElement>(null);
  useEffect(() => { setTerminou(false); }, [atual?.slug]);
  function irParaQuestionario() {
    setTerminou(false);
    questionarioRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  // "Proximo video" abre o seguinte JA TOCANDO (pedido dele, 30/09/2026: abria parado). So' por
  // este botao — clicar na lista da esquerda segue abrindo parado, pra quem so' quer olhar.
  const tocarAoAbrirRef = useRef(false);
  function irParaProximo(slugProximo: string) {
    tocarAoAbrirRef.current = true;
    navigate(`/treinamento/${slugProximo}`);
  }
  function assistirDeNovo() {
    const v = videoRef.current;
    setTerminou(false);
    if (v) { v.currentTime = 0; void v.play(); }
  }

  const modais = (
    <>
      {resultadosAbertos && <ModalResultados onFechar={() => setResultadosAbertos(false)} />}
      {gerenciarAberto && <ModalGerenciarVideos onFechar={() => { setGerenciarAberto(false); recarregar(); }} />}
    </>
  );

  if (!atual) {
    return (
      <div className="p-8 text-gray-600">
        {erro ?? (videos === null ? 'Carregando...' : 'Nenhum vídeo publicado.')}
        {podeGerenciar && videos !== null && (
          <button type="button" onClick={() => setGerenciarAberto(true)} className="btn-primario ml-3 px-3 py-1.5 rounded-md inline-flex items-center gap-2">
            <Settings size={16} /> Gerenciar vídeos
          </button>
        )}
        {modais}
      </div>
    );
  }

  return (
    <div className="manual-root flex h-[calc(100vh-3.5rem)]">
      {/* 506px, titulo em 14px e botoes alinhados a esquerda: pedidos dele (30/09/2026, dois prints
          com retangulo vermelho) — cabe o titulo inteiro, fica mais facil de ler e a lista chega
          perto do video. Era w-72 (252px) com 12,25px. */}
      <aside className="w-[506px] flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col">
        <div className="p-3 border-b border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <PlayCircle size={18} className="text-blue-600" />
            <h2 className="font-bold text-gray-700">Videos de Treinamento</h2>
          </div>
          {/* No topo, nao no pe: a coluna tem a altura da janela menos um cabecalho estimado, e no
              pe o botao ficava cortado em 1280x800. */}
          <button
            type="button"
            onClick={() => navigate('/manual')}
            className="w-full flex items-center justify-start gap-2 px-3 py-1.5 text-sm rounded-md btn-secundario"
          >
            <BookOpen size={16} /> Abrir o Manual escrito
          </button>
          {podeVerResultados && (
            <button
              type="button"
              onClick={() => setResultadosAbertos(true)}
              className="w-full mt-2 flex items-center justify-start gap-2 px-3 py-1.5 text-sm rounded-md btn-secundario"
            >
              <ClipboardList size={16} /> Resultados da equipe
            </button>
          )}
          {podeGerenciar && (
            <button
              type="button"
              onClick={() => setGerenciarAberto(true)}
              className="w-full mt-2 flex items-center justify-start gap-2 px-3 py-1.5 text-sm rounded-md btn-secundario"
            >
              <Settings size={16} /> Gerenciar vídeos
            </button>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-1" data-lista-videos>
          {secoes.map((s) => {
            const aberta = abertas.has(s.id);
            return (
              <div key={s.id}>
                <button
                  type="button"
                  onClick={() => alternar(s.id)}
                  aria-expanded={aberta}
                  className="w-full text-left px-3 py-2 flex items-center gap-1 font-bold text-gray-800 hover:bg-gray-100"
                >
                  {aberta ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <span className="flex-1 text-[14px]">{s.numero}. {s.nome}</span>
                  <span className="text-xs font-normal text-gray-500">
                    {s.videos.length} vídeo{s.videos.length === 1 ? '' : 's'}
                  </span>
                </button>
                {aberta && s.videos.map((v) => {
                  const isAtual = v.slug === atual.slug;
                  return (
                    <button
                      key={v.slug}
                      type="button"
                      onClick={() => navigate(`/treinamento/${v.slug}`)}
                      className={`w-full text-left pl-8 pr-3 py-2 text-sm transition border-l-4 ${
                        isAtual
                          ? 'bg-blue-50 border-blue-600 text-blue-900 font-medium'
                          : 'border-transparent text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="flex-1 text-[14px]">{v.numero} {v.titulo}</span>
                        <span className="text-xs text-gray-500">{v.duracao ?? ''}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>
      </aside>

      <div className="flex-1 overflow-y-auto bg-white">
        <div className="max-w-5xl mx-auto px-8 py-6">
          <div className="pagina-header">
            <h2 className="pagina-titulo">{atual.numero} {atual.titulo}</h2>
          </div>
          <div className="relative">
            {/* key: trocar de video recarrega o player do zero, senao ele continua no ponto do anterior. */}
            <video
              key={atual.slug}
              ref={videoRef}
              src={atual.arquivo}
              controls
              preload="metadata"
              onEnded={() => setTerminou(true)}
              onLoadedMetadata={(e) => {
                // Chegou pelo "Proximo video": ja toca (o clique dele no botao libera o som).
                if (tocarAoAbrirRef.current) {
                  tocarAoAbrirRef.current = false;
                  void e.currentTarget.play().catch(() => { /* navegador recusou: fica no play manual */ });
                }
              }}
              onPlay={() => setTerminou(false)}
              className="w-full aspect-video rounded-lg bg-black shadow"
            />
            {/* FIM DO VIDEO (pedido dele, 30/09/2026): o "Proximo video" do pe da pagina ficava a
                4,4 telas de rolagem do fim do 2.01 — quem acabava de assistir nao o via. Aqui o
                caminho aparece onde o olho ja esta. Questionario e proximo lado a lado e SEM
                destaque: o questionario nao trava o proximo (decisao dele), quem escolhe e' ela. */}
            {terminou && (
              <div className="absolute inset-0 rounded-lg flex flex-col items-center justify-center gap-4 p-6"
                style={{ background: 'rgba(0,0,0,0.82)' }} data-fim-video>
                <div className="text-white text-[18px] font-bold">
                  {proximo ? 'O que fazer agora?' : 'Você terminou todos os vídeos da trilha!'}
                </div>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {atual.perguntas.length > 0 && (
                    <button type="button" onClick={irParaQuestionario}
                      className="px-4 py-2.5 rounded-md font-bold text-[15px] inline-flex items-center gap-2"
                      style={{ background: '#fff', color: '#000' }}>
                      <ClipboardList size={18} /> Responder o questionário ({atual.perguntas.length} perguntas)
                    </button>
                  )}
                  {proximo && (
                    <button type="button" onClick={() => irParaProximo(proximo.slug)}
                      className="px-4 py-2.5 rounded-md font-bold text-[15px] inline-flex items-center gap-2"
                      style={{ background: '#fff', color: '#000' }}>
                      Próximo vídeo: {proximo.numero} {proximo.titulo} <ChevronRight size={18} />
                    </button>
                  )}
                </div>
                <button type="button" onClick={assistirDeNovo}
                  className="text-white underline text-[14px] inline-flex items-center gap-1">
                  <RotateCcw size={14} /> Assistir de novo
                </button>
              </div>
            )}
          </div>
          <p className="text-gray-700 leading-relaxed mt-4 text-[16px]">{atual.resumo}</p>
          <div ref={questionarioRef}>
            <Questionario slug={atual.slug} perguntas={atual.perguntas} />
          </div>
          {proximo && (
            <button
              type="button"
              onClick={() => irParaProximo(proximo.slug)}
              className="btn-primario mt-4 px-4 py-2 rounded-md inline-flex items-center gap-2"
            >
              Proximo video: {proximo.numero} {proximo.titulo} <ChevronRight size={16} />
            </button>
          )}
        </div>
      </div>
      {modais}
    </div>
  );
}
